import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useParty } from '../contexts/PartyContext';
import { useViewerIdentity } from '../hooks/useViewerIdentity';
import UserSongManager from './UserSongManager';
import AuthModal from './AuthModal';

const MobileFooter: React.FC = () => {
  const { user } = useAuth();
  const { currentParty, isPartyOwner } = useParty();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMySongs, setShowMySongs] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { viewerId } = useViewerIdentity();
  const partyId = currentParty?.id;
  const [lastPartyId, setLastPartyId] = useState<string | null>(null);

  // Track the last party ID in localStorage
  useEffect(() => {
    if (partyId) {
      localStorage.setItem('lastPartyId', partyId);
      setLastPartyId(partyId);
    } else {
      const stored = localStorage.getItem('lastPartyId');
      if (stored) {
        setLastPartyId(stored);
      }
    }
  }, [partyId]);

  // Determine party type
  const isKaraokeParty = !currentParty?.gameMode || currentParty.gameMode === 'karaoke';
  const isGameParty = currentParty?.gameMode === 'farkle' || currentParty?.gameMode === 'imposter';

  const showMySongsButton = useMemo(() => {
    if (!partyId) return false;
    if (isPartyOwner) return false;
    if (!isKaraokeParty) return false; // Hide for game parties
    if (user) return true;
    return Boolean(viewerId);
  }, [user, partyId, isPartyOwner, viewerId, isKaraokeParty]);

  // Show rejoin button if not currently on a party page but have a recent party
  const showRejoinButton = useMemo(() => {
    if (!lastPartyId) return false;
    // Don't show if already on a party page
    const isOnPartyPage = /^\/(party|join|search)\//.test(location.pathname);
    return !isOnPartyPage;
  }, [lastPartyId, location.pathname]);

  const handleRejoinParty = () => {
    if (lastPartyId) {
      // Determine the appropriate route based on whether user was a host or guest
      if (isPartyOwner && partyId === lastPartyId) {
        navigate(`/party/${lastPartyId}`);
      } else {
        navigate(`/search/${lastPartyId}`);
      }
    }
  };

  // Hide overlay entirely for hosts on the party display to keep UI clean
  if (/^\/party\//.test(location.pathname) && isPartyOwner) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden">
        <div className="bg-black/70 backdrop-blur border-t border-white/10 text-white">
          <div
            className="flex items-stretch justify-around py-2"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}
          >
            <button
              onClick={() => navigate('/')}
              className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
              aria-label="Home"
            >
              <div className="text-lg">🏠</div>
              <div className="text-xs">Home</div>
            </button>
            {/* Rejoin button — shown when user left a party and isn't on a party page */}
            {showRejoinButton && (
              <button
                onClick={handleRejoinParty}
                className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded animate-pulse"
                aria-label="Rejoin Party"
              >
                <div className="text-lg">🎉</div>
                <div className="text-xs text-qr-primary font-semibold">Rejoin</div>
              </button>
            )}
            {isKaraokeParty ? (
              <>
                {/* Only show Start button if not currently in a party */}
                {!partyId && (
                  <button
                    onClick={() => (user ? navigate('/dashboard') : navigate('/'))}
                    className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
                    aria-label="Start"
                  >
                    <div className="text-lg">🚀</div>
                    <div className="text-xs">Start</div>
                  </button>
                )}
                {showMySongsButton && (
                  <button
                    onClick={() => setShowMySongs(true)}
                    className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
                    aria-label="My Songs"
                  >
                    <div className="text-lg">🎤</div>
                    <div className="text-xs">My Songs</div>
                  </button>
                )}
              </>
            ) : isGameParty ? (
              <>
                {/* Only show Games button if not currently in a party */}
                {!partyId && (
                  <button
                    onClick={() => (user ? navigate('/dashboard') : navigate('/'))}
                    className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
                    aria-label="Games"
                  >
                    <div className="text-lg">🎮</div>
                    <div className="text-xs">Games</div>
                  </button>
                )}
              </>
            ) : (
              /* Show Start button when no party context */
              !partyId && (
                <button
                  onClick={() => (user ? navigate('/dashboard') : navigate('/'))}
                  className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
                  aria-label="Start"
                >
                  <div className="text-lg">🚀</div>
                  <div className="text-xs">Start</div>
                </button>
              )
            )}
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
                aria-label="Account"
              >
                <div className="text-lg">👤</div>
                <div className="text-xs">Account</div>
              </button>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
                aria-label="Sign In"
              >
                <div className="text-lg">🔑</div>
                <div className="text-xs">Sign In</div>
              </button>
            )}
          </div>
        </div>
      </div>
      {partyId && (
        <UserSongManager
          partyId={partyId}
          isOpen={showMySongs}
          onClose={() => setShowMySongs(false)}
        />
      )}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
};

export default MobileFooter;
    